import { Suspense, use, type ComponentType, type ReactNode } from "react";
import { useMatches } from "react-router";
import { Table } from "@/components/application/table/table";
import { cx } from "@/utils/cx";

/**
 * A page's data is loaded after the page is on screen: a loader hands its request over unawaited
 * (`deferred`), the page shows at once with a skeleton of itself where the data will go, and
 * `<Deferred>` swaps the data in when it lands. A loader that awaits would hold the click on the old
 * page for the whole round trip. Awaiting is only for what decides what the page is (the gate).
 */

/** A loader's request, unawaited. A page left before its data arrives never reads it, so a rejection is marked handled; `<Deferred>` still sees it. */
export function deferred<T>(promise: Promise<T>): Promise<T> {
  promise.catch(() => undefined);
  return promise;
}

/** The part of a page that waits for its data: `fallback`, that part's skeleton, until it lands. A rejection reaches the route's ErrorBoundary. */
export function Deferred<T>({ data, fallback, children }: { data: Promise<T>; fallback: ReactNode; children: (value: T) => ReactNode }) {
  return (
    <Suspense fallback={fallback}>
      <Resolved data={data} render={children} />
    </Suspense>
  );
}

function Resolved<T>({ data, render }: { data: Promise<T>; render: (value: T) => ReactNode }) {
  return render(use(data));
}

/** A placeholder where data will be, sized by `className`. Decorative: `<Loading>` says the page is loading. */
export function Skeleton({ className }: { className?: string }) {
  return <span aria-hidden="true" className={cx("block h-4 w-24 flex-none animate-pulse rounded-md bg-quaternary motion-reduce:animate-none", className)} />;
}

/** Wraps a skeleton so a screen reader hears that the page is loading, not a page of blanks. */
export function Loading({ children }: { children: ReactNode }) {
  return (
    <>
      <span className="sr-only" role="status">
        正在载入…
      </span>
      {children}
    </>
  );
}

const times = (n: number) => Array.from({ length: n }, (_, i) => i);
const WIDTHS = ["w-40", "w-28", "w-32", "w-24", "w-36"];

/** A table with its real column headings and `rows` rows of placeholders. */
export function TableSkeleton({ columns, rows = 5, size, bordered }: { columns: string[]; rows?: number; size?: "sm" | "md"; bordered?: boolean }) {
  return (
    <Table aria-label="正在载入" {...(size ? { size } : {})}>
      <Table.Header {...(bordered === undefined ? {} : { bordered })}>
        {columns.map((label, i) => (
          <Table.Head key={i} id={`c${i}`} label={label} isRowHeader={i === 0} />
        ))}
      </Table.Header>
      <Table.Body>
        {times(rows).map((row) => (
          <Table.Row key={row} id={`r${row}`}>
            {columns.map((_, i) => (
              <Table.Cell key={i}>
                <Skeleton className={WIDTHS[(row + i) % WIDTHS.length]} />
              </Table.Cell>
            ))}
          </Table.Row>
        ))}
      </Table.Body>
    </Table>
  );
}

/** A label and the boxed value under it, as `CopyField` draws them. */
export function FieldSkeleton({ label }: { label: string }) {
  return (
    <div className="flex flex-col gap-1.5">
      <span className="text-sm font-medium text-secondary">{label}</span>
      <Skeleton className="h-9 w-full rounded-lg" />
    </div>
  );
}

/** What a fresh load shows while the gate reads the session: the skeleton the deepest matching route declares as `handle.skeleton`. */
export function RouteSkeleton() {
  const matches = useMatches();
  for (let i = matches.length - 1; i >= 0; i--) {
    const Page = (matches[i]!.handle as { skeleton?: ComponentType } | undefined)?.skeleton;
    if (Page) return <Page />;
  }
  return null;
}
